import { useMemo } from 'react';
import { useMantineTheme } from '@mantine/core';
import { useCITypeMapStorage } from '@/entities/srm/ci';
import {
  getTQLGraphElements,
  type TQLGraphResponse,
} from '@/entities/srm/tql-query';
import { useAppColorScheme } from '@/shared/lib/hooks/use-app-color-scheme';
import {
  BULK_LINK_PREVIEW_EDGE_CLASS,
  BULK_LINK_PREVIEW_SELECTED_EDGE_CLASS,
  hasGraphClass,
} from '@/shared/ui/graph-renderer/contract';
import {
  ICON_LABEL_SIZE,
  type SigmaGraphOptions,
} from '@/shared/ui/sigma-graph/options';

/** Sigma appearance shared by SRM canvases and dashboard graph views. */
export const useSigmaGraphOptions = () => {
  const { data: ciTypeMap } = useCITypeMapStorage();
  const theme = useMantineTheme();
  const colorScheme = useAppColorScheme();
  return useMemo<SigmaGraphOptions>(
    () => ({
      layout: { backend: 'webgpu' },
      nodeAttributes: (node) => {
        const ciType = ciTypeMap.get(node.data.ci_type_name);
        return {
          color: ciType?.color || theme.colors.gray[6],
          image: ciType?.icon || undefined,
          label: node.data.ci_name ?? node.data.label ?? node.data.id,
          size: ciType?.icon_size ? ciType.icon_size / 20 : 3,
          type: 'image',
        };
      },
      edgeAttributes: (edge) => {
        const selected = hasGraphClass(
          edge.classes,
          BULK_LINK_PREVIEW_SELECTED_EDGE_CLASS,
        );
        const preview = hasGraphClass(
          edge.classes,
          BULK_LINK_PREVIEW_EDGE_CLASS,
        );
        return {
          color: selected
            ? theme.colors.green[6]
            : preview
              ? theme.colors.blue[5]
              : undefined,
          label:
            edge.data.label ?? edge.data.link?.label ?? edge.data.link?.name,
          size: selected ? 1.5 : preview ? 1 : 0.5,
        };
      },
      labelColor:
        colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.dark[6],
      labelSize: ICON_LABEL_SIZE,
      settings: { minEdgeThickness: 0.25 },
      edgeCreationPreview: {
        color: theme.colors.blue[5],
        targetColor: theme.colors.green[6],
        lineWidth: 1,
      },
    }),
    [
      ciTypeMap,
      colorScheme,
      theme.colors.blue,
      theme.colors.dark,
      theme.colors.gray,
      theme.colors.green,
    ],
  );
};

/** Shared SRM input mapping and Sigma appearance for editing and view canvases. */
export const useSigmaGraph = (graph: TQLGraphResponse | null) => {
  const elements = useMemo(() => getTQLGraphElements(graph), [graph]);
  const adapterOptions = useSigmaGraphOptions();
  return { elements, adapterOptions };
};